import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import Popup from "./pages/popup/popup";
import { useApp } from "./context/AppState";
import "./App.css";
import { useAnalytics } from "./hooks/useAnalytics";
import CartDrawerModal from "./components/Preview/Draggable/Block/CartDrawerBlock";
import WebsiteBackground from "./components/Preview/WebsiteBackground";
import ExtensionPageBackground from "./components/Preview/ExtensionPageBackground";
import {
  EXTENSION_PAGE,
  isCheckoutHostedExtensionPopup,
  isExtensionEditorSurfacePreview,
  resolveExtensionSurface,
} from "../shared/extension-preview.js";
import { resolveWatermarkVisibility } from "./utils/popup-watermark";
import WeupsellCartDrawerRuntime from "./components/CartDrawer/WeupsellCartDrawerRuntime";
import WeupsellThemeCartUpsellRuntime from "./components/CartDrawer/WeupsellThemeCartUpsellRuntime";
import WeupsellProductPageUpsellRuntime from "./components/ProductPageUpsell/WeupsellProductPageUpsellRuntime";
import { isCartProductOfferPopup } from "./utils/cartDrawerRuntime";
import { isProductPageSlotMode } from "../shared/product-page-slot.js";
import {
  getStaticCartDrawerSteps,
  isCustomCartDrawerThemeUpsellInjectMode,
  isStaticCustomCartDrawer,
  normalizeStaticCartDrawerPopup,
} from "./constants/custom-cart-drawer-template";
import { hasVisibleSchemaSteps } from "../shared/step-visibility.js";
import {
  isStaticProductPageUpsell,
  normalizeStaticProductPageUpsellPopup,
} from "./constants/product-page-upsell-template";
import { isVolumeDiscountPreview } from "./layouts/volume-discount/VolumeDiscountWidget";
import { ensureAiConversionLayoutCompatibility } from "./utils/aiConversionLayoutCompat";
import {
  isStorefrontProductPopup,
  isUpsellRecommendationProductPopup,
  normalizeProductUpsellRecommendationPopup,
} from "../shared/product-upsell-recommendation-defaults.js";
import { isProductRecommendationLayout, isProductRecommendationPopupLayout } from "../shared/product-recommendation-popup-schema.js";
import {
  isPopupLayout,
  POPUP_LAYOUT_IDS,
} from "./popup-layouts/index.js";
import {
  BUILTIN_LEGACY_FONT_FACES,
  resolveFontFamilyForStylesheet,
  THEME_DEFAULT_FONT,
} from "../shared/legacy-font-google.js";
import { exposeSyncStorefrontCartAfterAdd } from "./utils/sync-storefront-cart-after-add";

const SYSTEM_FONTS = new Set(
  [
    "inherit",
    THEME_DEFAULT_FONT,
    "theme",
    "default",
    "store-theme",
    "initial",
    "unset",
    "normal",
    "serif",
    "sans-serif",
    "monospace",
    "cursive",
    "fantasy",
    "system-ui",
    "-apple-system",
    "blinkmacsystemfont",
    "segoe ui",
    "roboto",
    "helvetica",
    "arial",
    "times new roman",
    "georgia",
    "courier new",
    "trebuchet ms",
    "verdana",
    "tahoma",
    "bahnschrift",
    "swiper-icons",
  ].map((x) => x.toLowerCase())
);

const normalizeFontCandidate = (raw = "") => {
  const first = String(raw || "")
    .split(",")[0]
    .replace(/["']/g, "")
    .trim();
  if (!first) return null;
  if (/^var\(/i.test(first)) return null;
  const cleaned = first.replace(/\s+/g, " ").trim();
  if (!/^[A-Za-z0-9 .+\-]+$/.test(cleaned)) return null;
  if (SYSTEM_FONTS.has(cleaned.toLowerCase())) return null;
  return cleaned;
};

const toSafeFontUrl = (raw) => {
  const value = String(raw || "").trim();
  if (!value) return null;
  try {
    const url = new URL(value, window.location.origin);
    if (!/^https?:$/i.test(url.protocol)) return null;
    return url.toString();
  } catch {
    return null;
  }
};

const isDirectFontUrl = (url) => /\.(woff2?|ttf|otf)(\?|#|$)/i.test(String(url || ""));

const isTrustedFontStylesheet = (url) =>
  /fonts\.(googleapis|bunny)\.(com|net)/i.test(String(url || "")) ||
  /fonts\.shopifycdn\.com/i.test(String(url || ""));

const getFontFormat = (url) => {
  const value = String(url || "").toLowerCase();
  if (value.includes(".woff2")) return "woff2";
  if (value.includes(".woff")) return "woff";
  if (value.includes(".ttf")) return "truetype";
  if (value.includes(".otf")) return "opentype";
  return "woff2";
};

const escapeCssString = (value) => String(value || "").replace(/\\/g, "\\\\").replace(/"/g, '\\"');

const collectPopupFontResources = (popup) => {
  const resources = {
    families: new Set(),
    directFamilies: new Set(),
    stylesheetUrls: new Set(),
    fontFaces: [],
  };
  if (!popup) return resources;

  const pushFont = (candidate) => {
    const normalized = normalizeFontCandidate(candidate);
    if (!normalized) return;
    const forStylesheet = resolveFontFamilyForStylesheet(normalized);
    if (forStylesheet) resources.families.add(forStylesheet);

    const builtin = BUILTIN_LEGACY_FONT_FACES.find((f) => f.family === normalized);
    if (builtin) {
      const key = builtin.family.toLowerCase();
      if (!resources.directFamilies.has(key)) {
        resources.directFamilies.add(key);
        resources.fontFaces.push({
          family: builtin.family,
          url: builtin.url,
          format: builtin.format,
        });
      }
    }
  };

  const pushFromCssObject = (css) => {
    if (!css || typeof css !== "object") return;
    pushFont(css.fontFamily);
  };

  const pushStylesheet = (url) => {
    const safeUrl = toSafeFontUrl(url);
    if (safeUrl && isTrustedFontStylesheet(safeUrl)) {
      resources.stylesheetUrls.add(safeUrl);
    }
  };

  const pushFontAsset = (asset) => {
    if (!asset || typeof asset !== "object") return;
    const family = normalizeFontCandidate(asset.family || asset.fontFamily);
    if (!family) return;

    resources.families.add(family);

    const directUrl =
      toSafeFontUrl(asset.woff2Url) ||
      toSafeFontUrl(asset.woffUrl) ||
      (isDirectFontUrl(asset.loadUrl) ? toSafeFontUrl(asset.loadUrl) : null);

    if (directUrl) {
      resources.directFamilies.add(family.toLowerCase());
      resources.fontFaces.push({
        family,
        url: directUrl,
        format: getFontFormat(directUrl),
      });
      return;
    }

    pushStylesheet(asset.cssUrl || asset.loadUrl);
  };

  pushFont(popup?.generalSettings?.fontFamily);
  pushFont(popup?.generalSettings?.bodyFontFamily);
  pushStylesheet(popup?.generalSettings?.googleFontsUrl);

  const assets = popup?.generalSettings?.brandFontAssets || {};
  pushFontAsset(assets.primary);
  pushFontAsset(assets.secondary);
  if (Array.isArray(assets.catalog)) {
    assets.catalog.forEach(pushFontAsset);
  }
  if (Array.isArray(assets.stylesheetUrls)) {
    assets.stylesheetUrls.forEach(pushStylesheet);
  }

  const typography = popup?.generalSettings?.brandTypography || {};
  Object.values(typography).forEach((role) => {
    pushFont(role?.fontFamily);
    pushFont(role?.fontName);
    pushStylesheet(role?.fontUrl);
    pushFontAsset(role);
  });

  if (Array.isArray(popup?.schema)) {
    popup.schema.forEach((step) => {
      pushFromCssObject(step?.popupSettings?.customCSS);
      pushFromCssObject(step?.popupSettings?.customCSS?.desktop);
      pushFromCssObject(step?.popupSettings?.customCSS?.mobile);

      const sections = [
        ...(Array.isArray(step?.section) ? step.section : []),
        ...(Array.isArray(step?.sections) ? step.sections : []),
      ];
    sections.forEach((section) => {
      pushFromCssObject(section?.setting?.customCSS?.desktop);
      pushFromCssObject(section?.setting?.customCSS?.mobile);
      const nested = Array.isArray(section?.sections) ? section.sections : [];
      nested.forEach((child) => {
        pushFromCssObject(child?.setting?.customCSS?.desktop);
        pushFromCssObject(child?.setting?.customCSS?.mobile);
      });
    });
    });
  }

  return resources;
};

const buildGoogleFontsUrl = (families = []) => {
  if (!families.length) return null;
  const params = families
    .map(
      (name) =>
        `family=${encodeURIComponent(name).replace(/%20/g, "+")}:wght@400;500;600;700`
    )
    .join("&");
  return `https://fonts.googleapis.com/css2?${params}&display=swap`;
};

const buildBunnyFontsUrl = (families = []) => {
  if (!families.length) return null;
  const familyParam = families
    .map((name) => `${encodeURIComponent(name).replace(/%20/g, "+")}:400,500,600,700`)
    .join("|");
  return `https://fonts.bunny.net/css?family=${familyParam}&display=swap`;
};

const upsertFontLink = (id, href) => {
  const existing = document.getElementById(id);
  if (!href) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    existing.setAttribute("href", href);
    return;
  }
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
};

const upsertFontStyle = (id, cssText) => {
  const existing = document.getElementById(id);
  if (!cssText) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    existing.textContent = cssText;
    return;
  }
  const style = document.createElement("style");
  style.id = id;
  style.textContent = cssText;
  document.head.appendChild(style);
};

const syncDynamicFonts = (fontResources) => {
  const resources = fontResources || {
    families: new Set(),
    directFamilies: new Set(),
    stylesheetUrls: new Set(),
    fontFaces: [],
  };
  const directFamilies = resources.directFamilies || new Set();
  const families = [...(resources.families || [])].filter(
    (family) => !directFamilies.has(String(family).toLowerCase())
  );
  const stylesheetUrls = [...(resources.stylesheetUrls || [])];
  const fontFaces = resources.fontFaces || [];

  if (!families.length) {
    upsertFontLink("weupsell-dynamic-google-fonts", null);
    upsertFontLink("weupsell-dynamic-bunny-fonts", null);
  } else {
    // Multi-provider strategy:
    // 1) Google Fonts (primary)
    // 2) Bunny Fonts (free fallback mirror for many families)
    upsertFontLink("weupsell-dynamic-google-fonts", buildGoogleFontsUrl(families));
    upsertFontLink("weupsell-dynamic-bunny-fonts", buildBunnyFontsUrl(families));
  }

  const previousLinks = document.querySelectorAll("[data-weupsell-brand-font-stylesheet]");
  previousLinks.forEach((node) => node.remove());
  stylesheetUrls.forEach((href, index) => {
    upsertFontLink(`weupsell-brand-font-stylesheet-${index}`, href);
    const link = document.getElementById(`weupsell-brand-font-stylesheet-${index}`);
    if (link) link.setAttribute("data-weupsell-brand-font-stylesheet", "true");
  });

  const uniqueFaces = [];
  const seen = new Set();
  BUILTIN_LEGACY_FONT_FACES.forEach((face) => {
    const key = `${face.family}|${face.url}`;
    if (seen.has(key)) return;
    seen.add(key);
    uniqueFaces.push(face);
  });
  fontFaces.forEach((face) => {
    const key = `${face.family}|${face.url}`;
    if (!face.family || !face.url || seen.has(key)) return;
    seen.add(key);
    uniqueFaces.push(face);
  });

  const cssText = uniqueFaces
    .map(
      (face) => `@font-face {
  font-family: "${escapeCssString(face.family)}";
  src: url("${escapeCssString(face.url)}") format("${face.format}");
  font-weight: 100 900;
  font-style: normal;
  font-display: swap;
}`
    )
    .join("\n");
  upsertFontStyle("weupsell-dynamic-font-faces", cssText);
};

/** Admin iframe sends `popupValue` as an array of popups; normalize to a flat list. */
const normalizeIncomingPopupList = (raw) => {
  if (raw == null) return [];
  const top = Array.isArray(raw) ? raw : [raw];
  return top
    .flatMap((item) => (Array.isArray(item) ? item : [item]))
    .filter((item) => item && typeof item === "object")
    .map((popup) => {
      const withAi = ensureAiConversionLayoutCompatibility(popup);
      if (
        isStaticCustomCartDrawer(withAi) ||
        withAi?.popUpType === "Custom Cart" ||
        withAi?.generalSettings?.isStaticCartDrawer
      ) {
        return normalizeStaticCartDrawerPopup(withAi);
      }
      if (
        isStaticProductPageUpsell(withAi) ||
        withAi?.popUpType === "Product Page Upsell" ||
        withAi?.generalSettings?.isStaticProductPageUpsell
      ) {
        return normalizeStaticProductPageUpsellPopup(withAi);
      }
      if (isStorefrontProductPopup(withAi)) {
        if (isProductRecommendationLayout(withAi)) {
          return withAi;
        }
        if (isUpsellRecommendationProductPopup(withAi)) {
          return normalizeProductUpsellRecommendationPopup(withAi);
        }
        return withAi;
      }
      return withAi;
    });
};

function MainApp() {
  const [initialLoading, setInitialLoading] = useState(true);
  const [initialAnalyticsLoading, setInitialAnalyticsLoading] = useState(true);
  const { analyticsDisabled, getLocationInfo, addAnalytics } = useAnalytics();
  const { dispatch, cartOpen, setCartOpen, appState, setPreviewDeviceIsMobile } =
    useApp();
  const location = useLocation();
  const [data, setData] = useState(null);
    const [popupValues, setPopupValues] = useState([]); // Accumulate multiple popups
  const dismissedPopupOpenGenerationsRef = useRef(new Map());
  const lastPopupOpenGenerationRef = useRef(new Map());
  // Removed lastEvent since we no longer need trigger event handling

  const initializeAnalytics = useCallback(async () => {
    setInitialAnalyticsLoading(true);
    if (analyticsDisabled) {
      setInitialAnalyticsLoading(false);
      return;
    }

    const localUserId = localStorage.getItem("user-id");
    const pageViews = JSON.parse(
      localStorage.getItem("user-pageViews") || "[]"
    );
    const isPageViewExists = pageViews?.find(
      (pageView) => pageView?.pathname === location?.pathname
    );

    if (!localUserId || !isPageViewExists) {
      const userId = localUserId || `user-${uuidv4()}`;
      const pageViewId = isPageViewExists?.pageViewID || `pageView-${uuidv4()}`;
      const device = {
        width: window.innerWidth,
        height: window.innerHeight
      };

      localStorage.setItem("user-id", userId);
      localStorage.setItem("user-popup-impression", JSON.stringify([]));
      if (!isPageViewExists) {
        localStorage.setItem(
          "user-pageViews",
          JSON.stringify([
            ...pageViews,
            {
              device,
              pathname: location?.pathname,
              pageViewID: pageViewId,
              timeStamp: Date.now(),
            },
          ])
        );
      }

      const locationData = await getLocationInfo();

      await addAnalytics({
        device,
        location: locationData,
        userId,
        pageViewId,
        pathname: location.pathname,
      });
    }
    setInitialAnalyticsLoading(false);
  }, [location?.pathname, getLocationInfo, addAnalytics, analyticsDisabled]);

  const initializeStore = useCallback(async () => {
    const shopDomain = window?.Shopify?.shop || window.location.hostname;
    const shopify = window?.Shopify;
    const cfg = window?.weupsellConfig || {};
    const embeddedShop = cfg.popupData?.shop || {};
    const embeddedWatermark = cfg.popupData?.watermark;
    const hideWatermarkFromMetadata =
      embeddedWatermark?.hideWatermark === true ||
      embeddedWatermark?.enabled === false;
    const shop = {
      shop: shopDomain,
      currency:
        shopify?.currency?.active ||
        shopify?.currency?.currencyCode ||
        embeddedShop.currency ||
        cfg.currency ||
        undefined,
      money_format:
        cfg.moneyFormat ||
        shopify?.money_format ||
        shopify?.moneyFormat ||
        embeddedShop.money_format ||
        embeddedShop.moneyFormat ||
        cfg.money_format,
      moneyFormat:
        shopify?.money_format ||
        shopify?.moneyFormat ||
        embeddedShop.moneyFormat ||
        embeddedShop.money_format ||
        cfg.moneyFormat ||
        cfg.money_format,
      ...(hideWatermarkFromMetadata ? { hideWatermark: true } : {}),
    };
    dispatch({
      type: "STORE_INIT",
      payload: {
        shop,
        is_preview_mode: false,
        watermark: resolveWatermarkVisibility(embeddedWatermark, shop),
        planPaywall: cfg.popupData?.planPaywall || { overLimit: false },
      },
    });
    setInitialLoading(false);
  }, [dispatch]);

  const initializeStoreValue = useCallback(async (newProps) => {
    if (typeof newProps?.previewMobileView === "boolean") {
      setPreviewDeviceIsMobile(newProps.previewMobileView);
    }

    const finalSchema = {
      popUpType: newProps?.popUpType,
      activeStep: newProps?.activeStep,
      shop: newProps?.shop,
      popupValue: newProps?.popupValue,
      is_preview_mode: newProps?.is_preview_mode,
      extensionSurface: newProps?.extensionSurface ?? null,
      previewChrome: newProps?.previewChrome ?? null,
    };

    await dispatch({
      type: "STORE_INIT",
      payload: {
        shop: finalSchema.shop,
        is_preview_mode: finalSchema?.is_preview_mode,
        watermark: resolveWatermarkVisibility(newProps?.watermark, newProps?.shop),
        planPaywall: newProps?.planPaywall || { overLimit: false },
      },
    });

    if (finalSchema?.is_preview_mode) {
      dispatch({
        type: "POPUP_INIT",
        payload: finalSchema
      });
    }

    setInitialAnalyticsLoading(false);
    setInitialLoading(false);

  }, [dispatch, setPreviewDeviceIsMobile]);

  const applyPreviewProps = useCallback(
    (newProps) => {
      if (!newProps) return;
      if (typeof window !== "undefined" && window.__WEUPSELL_PREVIEW_LAYOUT_DRAGGING__) {
        return;
      }
      window.__WEUPSELL_PREVIEW_PROPS__ = newProps;
      initializeStoreValue(newProps);
      // Storefront accumulates popups in the message handler; replacing here drops earlier slots.
      if (newProps.popupValue && !window?.isLiveModeTrigger) {
        setPopupValues(normalizeIncomingPopupList(newProps.popupValue));
      }
    },
    [initializeStoreValue]
  );

  // Define the function to update the props
  window.updatePropsInReactApp = function (newProps) {
    if (newProps) {
      applyPreviewProps(newProps);
    }
  };

  useEffect(() => {
    // Admin iframe preview never runs storefront analytics.
    if (!window?.isLiveModeTrigger) {
      setInitialAnalyticsLoading(false);
      return;
    }
    // Live storefront: init analytics in background — never block popup UI.
    setInitialAnalyticsLoading(false);
  }, []);

  useEffect(() => {
    // Apply any props that arrived before React finished registering callbacks.
    if (!window?.isLiveModeTrigger && window.__WEUPSELL_PREVIEW_PROPS__) {
      applyPreviewProps(window.__WEUPSELL_PREVIEW_PROPS__);
    }
  }, [applyPreviewProps]);

  useEffect(() => {
    if (window?.isLiveModeTrigger) return undefined;

    const notifyParentReady = () => {
      try {
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: "PREVIEW_READY" }, window.location.origin);
        }
      } catch {
        // ignore cross-origin parent errors
      }
    };

    notifyParentReady();
    return undefined;
  }, []);

  useEffect(() => {
    // IF SHOPIFY EMBEDDED THEME LOADED isLiveModeTrigger then the intialize store will be call
    if (window?.isLiveModeTrigger) {
      exposeSyncStorefrontCartAfterAdd();
      initializeStore();
    }
  }, [window?.isLiveModeTrigger, initializeStore]);

  useEffect(() => {
    if (window?.isLiveModeTrigger && location) {
      initializeAnalytics();
    }
  }, [window?.isLiveModeTrigger, location?.pathname, initializeAnalytics])

  const openStaticCartDrawerFromRuntime = useCallback(() => {
    try {
      document
        .querySelectorAll("cart-drawer, #cart-drawer, .cart-drawer")
        .forEach((el) => {
          if (typeof el.close === "function") el.close();
          el.classList.remove("active", "is-open", "drawer--is-open", "menu-opening");
          el.removeAttribute("open");
        });
    } catch (_) {}
    setCartOpen(true);
  }, [setCartOpen]);

  useEffect(() => {
    const onRequestClose = () => {
      setCartOpen(false);
      try {
        window.weupsellCancelStaticCartDrawerNudges?.();
      } catch (_) {}
    };
    window.addEventListener(
      "weupsell:request-static-cart-drawer-close",
      onRequestClose
    );
    return () => {
      window.removeEventListener(
        "weupsell:request-static-cart-drawer-close",
        onRequestClose
      );
    };
  }, [setCartOpen]);

  useEffect(() => {
    const handler = (e) => {
      const msg = e?.data;

      if (msg?.type === "PREVIEW_DEVICE_VIEW") {
        if (typeof msg.previewMobileView === "boolean") {
          setPreviewDeviceIsMobile(msg.previewMobileView);
        }
        const cached = window.__WEUPSELL_PREVIEW_PROPS__;
        if (cached) {
          applyPreviewProps(cached);
        }
        return;
      }

      if (msg?.type === "UPDATE_PROPS" && msg.props) {
        if (
          typeof window !== "undefined" &&
          window.__WEUPSELL_PREVIEW_LAYOUT_DRAGGING__
        ) {
          return;
        }
        setData(msg.props);
        applyPreviewProps(msg.props);

        if (msg.props.popupValue) {
          const incoming = normalizeIncomingPopupList(msg.props.popupValue);
          const openGeneration = Number(msg.props.popupOpenGeneration) || 0;
          setPopupValues((prev) => {
            let next = [...prev];
            for (const popup of incoming) {
              if (!popup || typeof popup !== "object") continue;
              const popupId = popup?._id;
              const exists = next.some((p) => p?._id === popupId);
              if (popupId && openGeneration > 0) {
                lastPopupOpenGenerationRef.current.set(popupId, openGeneration);
              }
              if (exists) {
                next = next.map((p) => (p._id === popupId ? popup : p));
                if (popupId) {
                  dismissedPopupOpenGenerationsRef.current.delete(popupId);
                }
              } else if (popupId) {
                const dismissedGeneration =
                  dismissedPopupOpenGenerationsRef.current.get(popupId);
                if (
                  dismissedGeneration != null &&
                  dismissedGeneration === openGeneration
                ) {
                  continue;
                }
                dismissedPopupOpenGenerationsRef.current.delete(popupId);
                next = [...next, popup];
              } else {
                next = [...next, popup];
              }
            }
            return next;
          });
        }
      }

      if (msg?.type === "OPEN_STATIC_CART_DRAWER") {
        console.log("[CartDrawerOpen][preview] OPEN_STATIC_CART_DRAWER received", {
          source: msg.source,
          pendingCartAdd: msg.pendingCartAdd,
        });
        if (msg.pendingCartAdd) {
          window.__WEUPSELL_CART_ADD_PENDING__ = true;
        }
        try {
          window.dispatchEvent(
            new CustomEvent("weupsell:open-static-cart-drawer", {
              detail: { pendingCartAdd: !!msg.pendingCartAdd },
            })
          );
        } catch (_) {}
        openStaticCartDrawerFromRuntime();
      }

      if (msg?.type === "POPUP_CLOSE" && msg.props?.popupValue) {
        console.log("Popup closed:", msg.props.reason);
        const popupToClose = msg.props.popupValue;
        const popupId = popupToClose?._id;
        if (popupId) {
          const openGeneration =
            lastPopupOpenGenerationRef.current.get(popupId) || 0;
          dismissedPopupOpenGenerationsRef.current.set(popupId, openGeneration);
        }
        setPopupValues((prev) => prev.filter((p) => p._id !== popupToClose._id));
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [applyPreviewProps, openStaticCartDrawerFromRuntime]);

  useEffect(() => {
    const merged = {
      families: new Set(),
      directFamilies: new Set(),
      stylesheetUrls: new Set(),
      fontFaces: [],
    };
    popupValues.forEach((popup) => {
      const resources = collectPopupFontResources(popup);
      resources.families?.forEach((family) => merged.families.add(family));
      resources.directFamilies?.forEach((family) =>
        merged.directFamilies.add(family)
      );
      resources.stylesheetUrls?.forEach((url) => merged.stylesheetUrls.add(url));
      if (resources.fontFaces?.length) {
        merged.fontFaces.push(...resources.fontFaces);
      }
    });
    syncDynamicFonts(merged);
  }, [popupValues]);

  const normalPopupValues = useMemo(
    () =>
      popupValues.filter((popup) => {
        if (isCheckoutHostedExtensionPopup(popup)) return false;
        if (isCartProductOfferPopup(popup)) return false;
        if (
          (isStaticProductPageUpsell(popup) || isVolumeDiscountPreview(popup)) &&
          isProductPageSlotMode(popup) &&
          !appState?.is_preview_mode
        ) {
          return false;
        }
        if (isStaticCustomCartDrawer(popup) && !appState?.is_preview_mode) {
          return getStaticCartDrawerSteps(popup).hasTrigger;
        }
        if (!appState?.is_preview_mode && !hasVisibleSchemaSteps(popup)) {
          return false;
        }
        return true;
      }),
    [popupValues, appState?.is_preview_mode]
  );

  const cartDrawerPopupValues = useMemo(
    () => popupValues.filter((popup) => isCartProductOfferPopup(popup)),
    [popupValues]
  );

  const productPageUpsellPopupValues = useMemo(
    () =>
      popupValues.filter(
        (popup) =>
          (isStaticProductPageUpsell(popup) || isVolumeDiscountPreview(popup)) &&
          isProductPageSlotMode(popup) &&
          hasVisibleSchemaSteps(popup)
      ),
    [popupValues]
  );

  const staticCustomCartPopup = useMemo(
    () => popupValues.find((popup) => isStaticCustomCartDrawer(popup)),
    [popupValues]
  );

  const themeCartUpsellPopupValues = useMemo(
    () =>
      popupValues.filter((popup) =>
        isCustomCartDrawerThemeUpsellInjectMode(popup)
      ),
    [popupValues]
  );

  const extensionEditorSurface = useMemo(
    () => resolveExtensionSurface(popupValues?.[0], appState?.extensionSurface),
    [popupValues, appState?.extensionSurface]
  );
  const isExtensionEditorPreview = isExtensionEditorSurfacePreview(
    extensionEditorSurface
  );
  const isCartDrawerExtensionPreview =
    isExtensionEditorPreview &&
    extensionEditorSurface === EXTENSION_PAGE.CART_DRAWER;

  const isCatalogModalPreview = useMemo(() => {
    const popup = popupValues?.[0];
    if (!popup) return false;
    return (
      isProductRecommendationPopupLayout(popup) ||
      isPopupLayout(popup, POPUP_LAYOUT_IDS.AI_RECOMMENDATION_UPSELL)
    );
  }, [popupValues]);

  const isProductPageUpsellPreview = useMemo(
    () => popupValues?.[0] && isStaticProductPageUpsell(popupValues[0]),
    [popupValues]
  );

  if (initialLoading || initialAnalyticsLoading) {
    // Never show raw "loading..." on the live storefront (shadow host is visible).
    if (window?.isLiveModeTrigger) {
      return null;
    }
    return <p>loading...</p>;
  }

  // Show website background in preview mode
  if (appState?.is_preview_mode) {
    return (
      <div
        className={`App App--preview${
          isCatalogModalPreview ? " App--catalog-modal-preview" : ""
        }${
          isProductPageUpsellPreview ? " App--product-page-upsell-preview" : ""
        }${
          isExtensionEditorPreview
            ? ` App--extension-preview App--extension-preview-surface${
                isCartDrawerExtensionPreview
                  ? " App--extension-preview-cart-drawer"
                  : ""
              }`
            : ""
        }`}
      >
        {isExtensionEditorPreview ? (
          <ExtensionPageBackground surface={extensionEditorSurface} />
        ) : isProductPageUpsellPreview ||
          appState?.previewChrome === "widget" ? null : (
          <WebsiteBackground
            template={popupValues?.[0]}
            shopData={data?.shop?.shop}
          />
        )}
        <Popup popupValues={popupValues} />
        <CartDrawerModal
          open={cartOpen}
          onClose={() => {
            setCartOpen(false);
            window.weupsellCancelStaticCartDrawerNudges?.();
            window.dispatchEvent(
              new CustomEvent("weupsell:static-cart-drawer-close")
            );
          }}
          popupData={staticCustomCartPopup}
        />
      </div>
    );
  }

  return (
    <div className="App">
      <Popup popupValues={normalPopupValues} />
      <WeupsellCartDrawerRuntime popupValues={cartDrawerPopupValues} />
      <WeupsellThemeCartUpsellRuntime popupValues={themeCartUpsellPopupValues} />
      <WeupsellProductPageUpsellRuntime
        popupValues={productPageUpsellPopupValues}
      />
      <CartDrawerModal
        open={cartOpen && !isCustomCartDrawerThemeUpsellInjectMode(staticCustomCartPopup)}
        onClose={() => {
          setCartOpen(false);
          window.weupsellCancelStaticCartDrawerNudges?.();
          window.dispatchEvent(
            new CustomEvent("weupsell:static-cart-drawer-close")
          );
        }}
        popupData={staticCustomCartPopup}
      />
    </div>
  );
}

function App() {
  return <MainApp />;
}

export default App;
